// Material endpoints (classroom PDFs and recorded-session links).
//
// Uploads never come through here: the file goes straight from the browser to
// Cloudflare R2 (see hooks/useR2Upload.js) and only the resulting URL is sent,
// in `document`. The `link` column is vestigial — never written.

import { get, post, patch, del } from './client';
import { toUiMaterial, materialToApi } from './adapters';

/**
 * "This topic has no materials" is reported as an ERROR, not an empty list, and
 * the status can't be trusted to identify it: the controller raises
 * `AppError(msg, httpStatus.NOT_FOUND)`, but http.status.js has no NOT_FOUND, so
 * statusCode ends up undefined and the error handler's `statusCode || 400`
 * emits 400. Verified against the live server: HTTP 400,
 * { status: 'fail', data: { message: 'No materials found for topicId 1' } }.
 *
 * So the message is what identifies it — matched narrowly, and only for 4xx, so
 * a genuine authorisation or server failure still surfaces as an error.
 */
const noMaterials = (status, payload) =>
  status >= 400 && status < 500
  && /no materials found/i.test(payload?.data?.message || payload?.message || '');

const EMPTY = { materials: [] };

// GET /material/getMaterialByTopicId/:topicId
export async function listByTopic(topicId) {
  const data = await get(`/material/getMaterialByTopicId/${encodeURIComponent(topicId)}`, {
    isEmpty: noMaterials,
    emptyValue: EMPTY,
  });
  const rows = Array.isArray(data?.materials) ? data.materials : [];
  return rows.map(toUiMaterial);
}

// GET /material/getAllMaterials — every material visible to the caller's group.
export async function listAll() {
  const data = await get('/material/getAllMaterials', { isEmpty: noMaterials, emptyValue: EMPTY });
  const rows = Array.isArray(data?.materials) ? data.materials : [];
  return rows.map(toUiMaterial);
}

// POST /material/createMaterial — `document` is either an uploaded R2 URL or a
// pasted link. Responds with { newMaterial }.
export async function createMaterial(form) {
  const data = await post('/material/createMaterial', materialToApi(form));
  return toUiMaterial(data?.newMaterial ?? data);
}

/**
 * PATCH /material/updateMaterial/:id
 *
 * Goes through `materialToApi` for the same reason create does: this route also
 * runs `checkInputData`, which 400s unless BOTH `title` and `topicId` are
 * present — and the UI form's own field names (`pages`, `file`, `url`) are not
 * columns, so sending them raw silently discarded the PDF and the description
 * while only the title survived.
 *
 * The server answers with a message and no row, so the caller rebuilds the row
 * from what it sent.
 */
export const updateMaterial = (id, form) =>
  patch(`/material/updateMaterial/${encodeURIComponent(id)}`, materialToApi(form));

export const deleteMaterial = (id) =>
  del(`/material/deleteMaterial/${encodeURIComponent(id)}`);
